// src/services/facilities.js - Updated with correct paths
import api from './api';

const handleApiError = (error, defaultMessage = 'An error occurred') => {
  console.error('API Error:', error);
  
  if (error.response?.data?.error) {
    throw new Error(error.response.data.error);
  } else if (error.response?.data?.message) {
    throw new Error(error.response.data.message);
  } else if (error.response) {
    throw new Error(error.response.statusText || defaultMessage);
  } else if (error.request) {
    throw new Error('Network error: Unable to connect to server');
  } else {
    throw new Error(error.message || defaultMessage);
  }
};

/**
 * Get all facilities
 */
export const getFacilities = async (params = {}) => {
  try {
    console.log('Fetching facilities with params:', params); // Debug log
    const response = await api.get('/facilities', { params });
    console.log('Facilities response:', response.data); // Debug log
    return response.data;
  } catch (error) {
    console.error('Get facilities error:', error.response || error); // Debug log
    throw handleApiError(error, 'Failed to fetch facilities');
  }
};

/**
 * Get public list of facilities (for registration)
 */
export const getPublicFacilities = async () => {
  try {
    const response = await api.get('/facilities/public/list');
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to fetch facilities');
  }
};

/**
 * Get facility by ID
 */
export const getFacility = async (facilityId) => {
  try {
    const response = await api.get(`/facilities/${facilityId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to fetch facility');
  }
};

/**
 * Create new facility (Admin only)
 */
export const createFacility = async (facilityData) => {
  try {
    const response = await api.post('/facilities', facilityData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to create facility');
  }
};

/**
 * Update facility (Admin only)
 */
export const updateFacility = async (facilityId, facilityData) => {
  try {
    const response = await api.put(`/facilities/${facilityId}`, facilityData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to update facility');
  }
};

/**
 * Delete facility (Admin only)
 */
export const deleteFacility = async (facilityId) => {
  try {
    const response = await api.delete(`/facilities/${facilityId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to delete facility');
  }
};

/**
 * Get facility statistics
 */
export const getFacilityStats = async (facilityId) => {
  try {
    const response = await api.get(`/facilities/${facilityId}/stats`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to fetch facility statistics');
  }
};

/**
 * Update facility status (Admin only)
 */
export const updateFacilityStatus = async (facilityId, status) => {
  try {
    const response = await api.patch(`/facilities/${facilityId}/status`, { status });
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to update facility status');
  }
};

/**
 * Get facility users
 */
export const getFacilityUsers = async (facilityId) => {
  try {
    const response = await api.get(`/facilities/${facilityId}/users`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to fetch facility users');
  }
};

/**
 * Upload facility logo (Admin only)
 */
export const uploadFacilityLogo = async (facilityId, logoFile) => {
  try {
    const formData = new FormData();
    formData.append('logo', logoFile);
    
    const response = await api.post(`/facilities/${facilityId}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to upload facility logo');
  }
};

/**
 * Get facility logo URL
 */
export const getFacilityLogo = async (facilityId) => {
  try {
    const response = await api.get(`/facilities/${facilityId}/logo`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to fetch facility logo');
  }
};

/**
 * Delete facility logo (Admin only)
 */
export const deleteFacilityLogo = async (facilityId) => {
  try {
    const response = await api.delete(`/facilities/${facilityId}/logo`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to delete facility logo');
  }
};

/**
 * Get DICOM/HL7 integration connection details for a facility (Admin only)
 */
export const getFacilityIntegration = async (facilityId) => {
  try {
    const response = await api.get(`/facilities/${facilityId}/integration`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to fetch integration settings');
  }
};

/**
 * Update DICOM/HL7 integration settings for a facility (Admin only)
 */
export const updateFacilityIntegration = async (facilityId, data) => {
  try {
    const response = await api.put(`/facilities/${facilityId}/integration`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to update integration settings');
  }
};

/**
 * Provision AWS SG rule (port 4242) + register scanner in Orthanc for a facility
 */
export const provisionFacilityAccess = async (facilityId) => {
  try {
    const response = await api.post(`/facilities/${facilityId}/integration/provision`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to provision facility access');
  }
};

/**
 * Remove AWS SG rule + deregister scanner from Orthanc for a facility
 */
export const deprovisionFacilityAccess = async (facilityId) => {
  try {
    const response = await api.delete(`/facilities/${facilityId}/integration/provision`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to deprovision facility access');
  }
};

/**
 * DICOM C-ECHO connectivity test via Orthanc for a facility
 */
export const testFacilityEcho = async (facilityId) => {
  try {
    const response = await api.post(`/facilities/${facilityId}/integration/echo`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to run C-ECHO test');
  }
};

/**
 * Get all eligible in-house radiologists (Admin only)
 */
export const getInHouseRadiologists = async () => {
  try {
    const response = await api.get('/facilities/in-house-radiologists');
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to fetch in-house radiologists');
  }
};

/**
 * Assign in-house radiologist to facility (Admin only)
 */
export const assignInHouseRadiologist = async (facilityId, data) => {
  try {
    const response = await api.put(`/facilities/${facilityId}/in-house-radiologist`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to assign in-house radiologist');
  }
};

export default {
  getFacilities,
  getPublicFacilities,
  getFacility,
  createFacility,
  updateFacility,
  deleteFacility,
  getFacilityStats,
  updateFacilityStatus,
  getFacilityUsers,
  uploadFacilityLogo,
  getFacilityLogo,
  deleteFacilityLogo,
  getFacilityIntegration,
  updateFacilityIntegration,
  provisionFacilityAccess,
  deprovisionFacilityAccess,
  testFacilityEcho,
  getInHouseRadiologists,
  assignInHouseRadiologist,
};